import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EventDetailed } from '../../../data-services/models/events/event-detailed.model';
import { EventsService } from '../../../data-services/events.service';
import { ActivatedRoute } from '@angular/router';
import { HeadlineComponent } from "../../headline/feature/headline.component";
import { EventTicketDetailed } from '../../../data-services/models/event-tickets/event-ticket-detailed.model';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { EventBookingComplete } from '../../../data-services/models/event-bookings/event-booking-complete.model';
import { WebBooking } from '../../../data-services/models/event-bookings/web-booking.model';
import { TicketPillComponent } from '../ticket-pill/feature/ticket-pill.component';
import { EventBasic } from '../../../data-services/models/events/event-basic.model';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { RouterModule } from '@angular/router';
import { DividerComponent } from '../../../core/divider/feature/divider.component';
import { EventBookingService } from '../../../data-services/event-booking.service';
import { PaypalComponent } from "../../../core/paypal/feature/paypal.component";
import { select, Store } from '@ngrx/store';
import { UserActions } from '../../../data-services/stores/actions/user.actions';
import { BoilerplateComponent } from '../../../core/boilerplate/feature/boilerplate.component';
import { AccordionComponent, AccordionPanelComponent } from '../../../core/accordion/feature/accordion.component';
import { selectEvent } from '../../../data-services/stores/selectors/events.selectors';
import { EventActions } from '../../../data-services/stores/actions/events.actions';
import { map, takeWhile, tap } from 'rxjs';
import { ProgressBarModule } from 'primeng/progressbar';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-event-booking',
  imports: [BoilerplateComponent, CommonModule,ProgressBarModule, RouterModule, HeadlineComponent, FormsModule, TicketPillComponent, ConfirmDialogModule, DividerComponent, ReactiveFormsModule, PaypalComponent, AccordionComponent, AccordionPanelComponent],
  templateUrl: '../ui/book.component.html',
  styleUrl: '../ui/book.component.css',
  providers: [CommonModule]
})
export class EventBookingComponent implements OnInit {
  @Input() lrpEvent?: EventDetailed
  returnedEventBooking?: EventBookingComplete;
  currentStep?: number = 1;
  bookingType?: string;
  playerTicketsArray: EventTicketDetailed[] = []
  crewTicketsArray: EventTicketDetailed[] = []
  staffTicketsArray: EventTicketDetailed[] = []
  mealTickets: EventTicketDetailed[] = []
  bunkTickets: EventTicketDetailed[] = []
  additionalTickets: EventTicketDetailed[] = [];
  optionalTickets: EventTicketDetailed[] = [];

  webBooking: WebBooking = {
    selectedPrimaryTicket: undefined,
    selectedAdditionalTickets: [],
    bookingForSelf: true,
    displayBooking: false,
    bookingType: '',
    bookingFirstname: '',
    bookingSurname: '',
    bookingEmail: '',
    event: this.lrpEvent
  }

  termsForm: FormGroup = new FormGroup({
    siteTerms: new FormControl<boolean>(false),
    systemTerms: new FormControl<boolean>(false),
    hostTerms: new FormControl<boolean>(false)
  })

  get ticketTotal(): number {
    return (this.webBooking.selectedPrimaryTicket?.cost ?? 0) + this.webBooking.selectedAdditionalTickets.flatMap(x => x.cost).reduce((acc, val) => acc + val, 0) + (this.webBooking.event?.organiser?.partnerFee ?? 0)
  }

  private readonly _eventService = inject(EventsService);
  private readonly _route = inject(ActivatedRoute);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly _bookingService = inject(EventBookingService);
  private readonly _store = inject(Store);
  private readonly _titleService = inject(Title)

  lrpEvent$ = this._store.select(selectEvent(this._route.snapshot.params['id'])).pipe(
    tap(x => {
      if (x && x?.eventTickets){
        this.lrpEvent = x;
        this.webBooking.event = this.lrpEvent;
        this._titleService.setTitle('LRPTickets.co.uk | Book Tickets for '+ (this.lrpEvent?.eventHost.eventSystem.name) + (this.lrpEvent?.eventHost.display ? ' - ' + this.lrpEvent?.eventHost.name + ' | ' : ' | ') + this.lrpEvent?.name)
        this.updateTickets();
      }
      else {
        this._store.dispatch(EventActions.loadSingleEvent({ eventRef: this._route.snapshot.params['id'], refresh: true }))
      }
    }),
  takeWhile(x => !x?.eventTickets)).subscribe()

  ngOnInit(): void {
    this._store.dispatch(EventActions.loadSingleEvent({ eventRef: this._route.snapshot.params['id'], refresh: true }))
  }

  updateTickets(){
    this.playerTicketsArray = (this.lrpEvent! as EventDetailed).eventTickets?.filter(x => this.arrayFilter(x,['Player','Player (Under 16)'])) ?? [];
    this.crewTicketsArray = (this.lrpEvent! as EventDetailed).eventTickets?.filter(x => this.arrayFilter(x,['Crew','Crew (Under 16)'])) ?? [];
    this.staffTicketsArray = (this.lrpEvent! as EventDetailed).eventTickets?.filter(x => this.arrayFilter(x,'Staff')) ?? [];
    this.mealTickets = (this.lrpEvent! as EventDetailed).eventTickets?.filter(x => this.arrayFilter(x,['Meal','Meal (Under 16)'])) ?? [];
    this.optionalTickets = (this.lrpEvent! as EventDetailed).eventTickets?.filter(x => this.arrayFilter(x,['Optional'])) ?? [];    
  }

  selectBookingType(type: string) {
    this.bookingType = type;
    this.currentStep = 2;
  }

  ableToBook(): boolean {
    const systemTerms: boolean = this.lrpEvent?.eventHost.eventSystem.terms ? this.termsForm.get('systemTerms')?.value : true;
    const hostTerms: boolean = this.lrpEvent?.eventHost.terms ? this.termsForm.get('hostTerms')?.value : true;
    return systemTerms && hostTerms && this.termsForm.get('siteTerms')?.value;
  }

  secureBooking() {
    this.confirmationService.confirm({
      header: this.lrpEvent?.name,
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Book My Tickets!',

      },
      accept: () => {
        this._bookingService.createEventBooking(this.webBooking).subscribe(
          result => {
            this.returnedEventBooking = result
            this.returnedEventBooking.event = this.lrpEvent as EventBasic;
            this.currentStep = 4;
            this._store.dispatch(UserActions.loadUser({ refresh: true}))
          }
        )
      },
      reject: () => {
        return;
      }
      
    })
  }

  arrayFilter(ticket: EventTicketDetailed, ticketType: string | string[] ): boolean {
    const typeArray: string[] = [];
    if (typeof ticketType === 'string'){
      typeArray.push(ticketType);
    }
    else{
      typeArray.push(...ticketType);
    }
    return typeArray.includes(ticket.name)
    && ticket !== undefined
    && this.availableDates(ticket.availableFrom,ticket.availableTo)
  }

  availableDates(dateFrom: Date, dateTo: Date): boolean {
    return new Date(dateFrom).setHours(23,59,59) <= new Date().setHours(23,59,59) 
      && new Date(dateTo).setHours(23,59,59) >= new Date().setHours(23,59,59)
  }

  setupAdditionalTickets(){
    this.additionalTickets = [];
    this.additionalTickets.push(...this.bunkTickets,...this.mealTickets,...this.optionalTickets);
  }

  availableTickets(): EventTicketDetailed[]{
    switch (this.bookingType){
      case 'player':
        this.bunkTickets = (this.lrpEvent! as EventDetailed).eventTickets?.filter(x => this.arrayFilter(x,'playerbunk')) ?? [];
        this.setupAdditionalTickets();
        if (this.playerTicketsArray.length === 1){
          this.webBooking.selectedPrimaryTicket = this.playerTicketsArray[0];
        }
        return this.playerTicketsArray;
      case 'crew':
        this.bunkTickets = (this.lrpEvent! as EventDetailed).eventTickets?.filter(x => this.arrayFilter(x,'monsterbunk')) ?? [];
        this.setupAdditionalTickets();
        if (this.crewTicketsArray.length === 1){
          this.webBooking.selectedPrimaryTicket = this.crewTicketsArray[0];
        }
        return this.crewTicketsArray;
      case 'staff':
        this.bunkTickets = (this.lrpEvent! as EventDetailed).eventTickets?.filter(x => this.arrayFilter(x,'staffbunk')) ?? [];
        this.setupAdditionalTickets();
        if (this.staffTicketsArray.length === 1){
          this.webBooking.selectedPrimaryTicket = this.staffTicketsArray[0];
        }
        return this.staffTicketsArray;
      default:
        return [];
    }
  }

  soldOut(totalSpaces: number | undefined, bookings: number | undefined){
    return (totalSpaces ?? 0) - (bookings ?? 0) <= 0
  }

  calculateSpaces(totalSpaces?:number, bookings?:number){
    const calculatedValue =(bookings ?? 0) / ((totalSpaces ?? 0) / 100)
    return calculatedValue < 100 ? calculatedValue.toFixed(0) : 100;
  }

  toggleSecondaryTicket(ticket:EventTicketDetailed){
    if (this.webBooking.selectedAdditionalTickets.includes(ticket)){
      this.webBooking.selectedAdditionalTickets = this.webBooking.selectedAdditionalTickets.filter(x => x !== ticket);                                                                                        
    }
    else{
      this.webBooking.selectedAdditionalTickets.push(ticket);
    }
  }

  isTicketListed(ticket:EventTicketDetailed):boolean {
    return this.webBooking.selectedAdditionalTickets.includes(ticket)
  }
}
